import { ChangeEvent } from "react";
import { formatString } from "@format-string/core";
import { Box, Grid, MenuItem } from "@mui/material";
import { useContextContrato } from "src/Contexts/Lancamentos/Contrato/ContratosContext";
import { IValuesFilterContrato } from "src/Contexts/Lancamentos/Contrato/ContratosTypes";
import { valuesDefaultFilterContrato } from "src/Contexts/Lancamentos/Contrato/valuesDefaultInputsContratos/ValueDefaultInputContratos";
import { UseAutoCompletedAll } from "src/Contexts/Utils/UseAutoCompletedAll";
import { AppTextField } from "src/Pages/components";
import { AutoCompleteCorretorGeneric } from "src/Pages/components/AutoCompleted/AutoCompleteCorretor";
import { AutoCompleteOperadoraFilter } from "src/Pages/components/AutoCompleted/Filters/AutoCompleteOperadora";
import { DividerCore } from "src/Pages/components/DividerCore/DividerCore";
import { ModalFilterCore } from "src/Pages/components/ModalFilterCore/Index";

import { useHttpTableContratos } from "../table/Hooks/useHttpTableContratos";
import { AutoCompleteAdministradoras } from "src/Pages/components/AutoCompleted/AutoCompleteAdministradoras";
import { AutoCompleteModalidade } from "src/Pages/components/AutoCompleted/AutoCompleteModalidade";

const dateRangeRowSx = {
  "& .MuiTextField-root": { marginBottom: 0 },
} as const;

export const FilterSidebar = () => {
  const { handleGet } = useHttpTableContratos();

  const { setValuesFilterContrato, valuesFilterContrato } =
    useContextContrato();

  const {
    valueAutoCompProdutorActive,
    valueAutoCompAdministradora,
    valueAutoCompOperadoraFilter,
    setValueAutoCompProdutorActive,
    setValueAutoCompOperadoraFilter,
    setValueAutoCompModalidade,
    valueAutoCompModalidade,
  } = UseAutoCompletedAll();

  const handleChange =
    (prop: keyof IValuesFilterContrato, option?: "CPF_CNPJ") =>
      (event: ChangeEvent<HTMLInputElement>) => {
        if (option === "CPF_CNPJ") {
          setValuesFilterContrato({
            ...valuesFilterContrato,
            [prop]: formatString({ type: "cpfOurCnpj", value: event.target.value, }),
          });
        } else {
          setValuesFilterContrato({ ...valuesFilterContrato, [prop]: event.target.value, });
        }
      };

  const ClearFilter = async () => {
    setValuesFilterContrato(valuesDefaultFilterContrato);
    setValueAutoCompOperadoraFilter({ id: "", label: "" });
    setValueAutoCompProdutorActive({ id: "", label: "" });
    setValueAutoCompModalidade({ id: "", label: "" });

    await handleGet({ isDeleted: false });
  };

  const FilterSearch = async () => {
    await handleGet({
      codProposta: valuesFilterContrato.cod_proposta,

      codeOperadora: valueAutoCompOperadoraFilter.id,
      codeCorretor: valueAutoCompProdutorActive.id,
      codeAdministradora: valueAutoCompAdministradora.id,
      modalidade_code: valueAutoCompModalidade.id,

      endDateSignature: valuesFilterContrato.endDateSignature,
      startDateSignature: valuesFilterContrato.startDateSignature,

      startEffectiveDate: valuesFilterContrato.startEffectiveDate,
      endEffectiveDate: valuesFilterContrato.endEffectiveDate,

      startDateEmission: valuesFilterContrato.startDateEmission,
      endDateEmission: valuesFilterContrato.endDateEmission,

      startVencimentoDate: valuesFilterContrato.startVencimentoDate,
      endVencimentoDate: valuesFilterContrato.endVencimentoDate,

      client: {
        nameOrRazao: valuesFilterContrato.nameOrRazao,
        cpfCnpj: valuesFilterContrato.cpfCnpj,
      },

      status: valuesFilterContrato.status,
      isDeleted: valuesFilterContrato.isDeleted,
    });
  };

  return (
    <ModalFilterCore
      FilterClear={ClearFilter}
      FilterSearch={FilterSearch}
      sxStyle={{ width: 800, maxWidth: "100%" }}
    >
      <DividerCore title="Cliente (PF/PJ)" sxStyle={{ marginTop: "12px" }} />

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <AppTextField
            label="Titular/Razão"
            sxStyle={{ opacity: "", pointerEvents: "auto", width: "100%", margin: 0 }}
            value={valuesFilterContrato.nameOrRazao}
            variant="outlined"
            onChange={handleChange("nameOrRazao")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AppTextField
            label="CPF/CNPJ"
            sxStyle={{ opacity: "", pointerEvents: "auto", width: "100%", margin: 0 }}
            value={valuesFilterContrato.cpfCnpj}
            variant="outlined"
            onChange={handleChange("cpfCnpj", "CPF_CNPJ")}
          />
        </Grid>
      </Grid>

      <DividerCore title="Proposta" />

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12}>
          <AppTextField
            label="Código proposta"
            sxStyle={{ opacity: "", pointerEvents: "auto", width: "100%", margin: 0 }}
            value={valuesFilterContrato.cod_proposta}
            variant="outlined"
            onChange={handleChange("cod_proposta")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sx={{ mt: "8px" }}>
              <Grid container spacing={1} alignItems="flex-end">
                <Grid item xs={12} sm={5.5}>
                  <AppTextField
                    label="Data assinatura"
                    sxStyle={{ opacity: "", pointerEvents: "auto", width: "100%", margin: 0 }}
                    type="date"
                    value={valuesFilterContrato.startDateSignature}
                    variant="outlined"
                    onChange={handleChange("startDateSignature")}
                  />
                </Grid>
                <Grid item xs={12} sm={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center", pb: { xs: 0, sm: 1 } }}>
                  <Box component="span" sx={{ fontSize: "0.85rem", color: "text.secondary", whiteSpace: "nowrap" }}>
                    até
                  </Box>
                </Grid>
                <Grid item xs={12} sm={5.5}>
                  <AppTextField
                    label="Data assinatura"
                    sxStyle={{ opacity: "", pointerEvents: "auto", width: "100%", margin: 0 }}
                    type="date"
                    value={valuesFilterContrato.endDateSignature}
                    variant="outlined"
                    onChange={handleChange("endDateSignature")}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sx={{ mt: "8px" }}>
              <Grid container spacing={1} alignItems="flex-end">
                <Grid item xs={12} sm={5.5}>
                  <AppTextField
                    label="Data emissão"
                    sxStyle={{ opacity: "", pointerEvents: "auto", width: "100%", margin: 0 }}
                    type="date"
                    value={valuesFilterContrato.startDateEmission}
                    variant="outlined"
                    onChange={handleChange("startDateEmission")}
                  />
                </Grid>
                <Grid item xs={12} sm={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center", pb: { xs: 0, sm: 1 } }}>
                  <Box component="span" sx={{ fontSize: "0.85rem", color: "text.secondary", whiteSpace: "nowrap" }}>
                    até
                  </Box>
                </Grid>
                <Grid item xs={12} sm={5.5}>
                  <AppTextField
                    label="Data emissão"
                    sxStyle={{ opacity: "", pointerEvents: "auto", width: "100%", margin: 0 }}
                    type="date"
                    value={valuesFilterContrato.endDateEmission}
                    variant="outlined"
                    onChange={handleChange("endDateEmission")}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sx={{ mt: "8px" }}>
              <Grid container spacing={1} alignItems="flex-end">
                <Grid item xs={12} sm={5.5}>
                  <AppTextField
                    label="Data vigência"
                    sxStyle={{ opacity: "", pointerEvents: "auto", width: "100%", margin: 0 }}
                    type="date"
                    value={valuesFilterContrato.startEffectiveDate}
                    variant="outlined"
                    onChange={handleChange("startEffectiveDate")}
                  />
                </Grid>
                <Grid item xs={12} sm={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center", pb: { xs: 0, sm: 1 } }}>
                  <Box component="span" sx={{ fontSize: "0.85rem", color: "text.secondary", whiteSpace: "nowrap" }}>
                    até
                  </Box>
                </Grid>
                <Grid item xs={12} sm={5.5}>
                  <AppTextField
                    label="Data vigência"
                    sxStyle={{ opacity: "", pointerEvents: "auto", width: "100%", margin: 0 }}
                    type="date"
                    value={valuesFilterContrato.endEffectiveDate}
                    variant="outlined"
                    onChange={handleChange("endEffectiveDate")}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sx={{ mt: "8px" }}>
              <Grid container spacing={1} alignItems="flex-end">
                <Grid item xs={12} sm={5.5}>
                  <AppTextField
                    label="Data vencimento"
                    sxStyle={{ opacity: "", pointerEvents: "auto", width: "100%", margin: 0 }}
                    type="date"
                    value={valuesFilterContrato.startVencimentoDate}
                    variant="outlined"
                    onChange={handleChange("startVencimentoDate")}
                  />
                </Grid>
                <Grid item xs={12} sm={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center", pb: { xs: 0, sm: 1 } }}>
                  <Box component="span" sx={{ fontSize: "0.85rem", color: "text.secondary", whiteSpace: "nowrap" }}>
                    até
                  </Box>
                </Grid>
                <Grid item xs={12} sm={5.5}>
                  <AppTextField
                    label="Data vencimento"
                    sxStyle={{ opacity: "", pointerEvents: "auto", width: "100%", margin: 0 }}
                    type="date"
                    value={valuesFilterContrato.endVencimentoDate}
                    variant="outlined"
                    onChange={handleChange("endVencimentoDate")}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>


        <Grid item xs={12} md={6}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12}>
              <AutoCompleteCorretorGeneric fullWidth label="Corretor" variant="outlined" />
            </Grid>

            <Grid item xs={12}>
              <AutoCompleteOperadoraFilter fullWidth label="Operadora" variant="outlined" />
            </Grid>

            <Grid item xs={12}>
              <AutoCompleteModalidade fullWidth label="Modalidade" variant="outlined" />
            </Grid>

            <Grid item xs={12}>
              <AutoCompleteAdministradoras fullWidth label="Administradoras" variant="outlined" />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <DividerCore title="Status / Excluídos" />

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <AppTextField
            label="Status"
            sxStyle={{ opacity: "", pointerEvents: "auto", m: 0, fontSize: "0.9rem", width: "100%" }}
            value={valuesFilterContrato.status}
            variant="outlined"
            onChange={handleChange("status")}
            select
            fullWidth
          >
            <MenuItem sx={{ fontSize: "0.9rem" }} value="ALL">Todos</MenuItem>

            <MenuItem sx={{ fontSize: "0.9rem" }} value="APPROVED">Aprovado</MenuItem>
            <MenuItem sx={{ fontSize: "0.9rem" }} value="PENDING">Pendente</MenuItem>
            <MenuItem sx={{ fontSize: "0.9rem" }} value="VALIDATION">Validação</MenuItem>
            <MenuItem sx={{ fontSize: "0.9rem" }} value="CANCELLED">Cancelado / Mantem parcelas abertas</MenuItem>
            <MenuItem sx={{ fontSize: "0.9rem" }} value="CANCELAR_DELETAR_PARCELAS">Cancelar - Deletar parcelas abertas</MenuItem>
            <MenuItem sx={{ fontSize: "0.9rem" }} value="NAO_EFETIVADO">Não efetivado - Deletar parcelas</MenuItem>
          </AppTextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <AppTextField
            label="Excluídos?"
            sxStyle={{ opacity: "", pointerEvents: "auto", m: 0, width: "100%" }}
            value={valuesFilterContrato.isDeleted}
            variant="outlined"
            onChange={handleChange("isDeleted")}
            select
            fullWidth
          >
            <MenuItem value="ALL">Todos</MenuItem>
            <MenuItem value="FALSE">Não</MenuItem>
            <MenuItem value="TRUE">Sim</MenuItem>
          </AppTextField>
        </Grid>
      </Grid>
    </ModalFilterCore>
  );
};
